import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Categories from './pages/Categories';
import Products from './pages/Products';
import ProductView from './pages/ProductView';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import MyBookings from './pages/MyBookings';
import MyProfile from './pages/MyProfile';
import MyAddress from './pages/MyAddress';

// Import components
import SideMenu from './components/SideMenu';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32', // Green color for fresh food theme
    },
    secondary: {
      main: '#FF8F00', // Orange accent
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
});

function App() {
  // Load initial state from localStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const savedAuth = localStorage.getItem('isAuthenticated');
    return savedAuth ? JSON.parse(savedAuth) : false;
  });
  
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('user', JSON.stringify(user));
  }, [user]);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setCart([]);
    // Clear localStorage on logout
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('cart');
    localStorage.removeItem('user');
  };

  const addToCart = (product) => {
    setCart(prevCart => {
      const productId = product.Id || product.id || product.productId;
      const existingItem = prevCart.find(item => {
        const itemId = item.Id || item.id || item.productId;
        return itemId === productId;
      });
      
      if (existingItem) {
        return prevCart.map(item => {
          const itemId = item.Id || item.id || item.productId;
          return itemId === productId
            ? { ...item, quantity: item.quantity + (product.quantity || 1) }
            : item;
        });
      } else {
        return [...prevCart, { ...product, quantity: product.quantity || 1 }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => {
      const itemId = item.Id || item.id || item.productId;
      return itemId !== productId;
    }));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart =>
        prevCart.map(item => {
          const itemId = item.Id || item.id || item.productId;
          return itemId === productId ? { ...item, quantity } : item;
        })
      );
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
                   <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>
          {isAuthenticated && (
            <SideMenu 
              onLogout={handleLogout} 
              mobileOpen={mobileOpen}
              onMobileToggle={handleMobileToggle}
            />
          )}
          <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%', overflowX: 'hidden' }}>
            <Routes>
              <Route 
                path="/login" 
                element={
                  !isAuthenticated ? (
                    <Login onLogin={handleLogin} />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } 
              />
              <Route 
                path="/register" 
                element={
                  !isAuthenticated ? (
                    <Register onLogin={handleLogin} />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } 
              />
              <Route 
                path="/" 
                element={
                  isAuthenticated ? (
                    <Home 
                      cart={cart}
                      addToCart={addToCart}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/categories" 
                element={
                  isAuthenticated ? (
                    <Categories />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/products" 
                element={
                  isAuthenticated ? (
                    <Products 
                      addToCart={addToCart}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/product/:id" 
                element={
                  isAuthenticated ? (
                    <ProductView 
                      addToCart={addToCart}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/cart" 
                element={
                  isAuthenticated ? (
                    <Cart 
                      cart={cart}
                      removeFromCart={removeFromCart}
                      updateCartQuantity={updateCartQuantity}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/checkout" 
                element={
                  isAuthenticated ? (
                    <Checkout 
                      cart={cart}
                      clearCart={clearCart}
                      user={user}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/my-bookings" 
                element={
                  isAuthenticated ? (
                    <MyBookings />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/my-profile" 
                element={
                  isAuthenticated ? (
                    <MyProfile user={user} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/my-address" 
                element={
                  isAuthenticated ? (
                    <MyAddress />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
